import React, { useEffect, useRef, useState } from 'react';
import './App.css';

function App() {
  const canvasRef = useRef(null);
  const containerRef = useRef(null);
  const metaverseSectionRef = useRef(null);
  const [mousePos, setMousePos] = useState({ x: 0, y: 0 });
  const [scrollY, setScrollY] = useState(0);
  const [sphereScale, setSphereScale] = useState(0.3);
  const animationRef = useRef(null);
  const particlesRef = useRef([]);
  const timeRef = useRef(0);

  // Initialize particles for metaverse effect
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    const resizeCanvas = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };
    resizeCanvas();
    window.addEventListener('resize', resizeCanvas);

    // Create particles
    const particleCount = 150;
    particlesRef.current = Array.from({ length: particleCount }, () => ({
      x: Math.random() * canvas.width,
      y: Math.random() * canvas.height,
      z: Math.random() * 1000,
      vx: (Math.random() - 0.5) * 0.5,
      vy: (Math.random() - 0.5) * 0.5,
      vz: (Math.random() - 0.5) * 2,
      size: Math.random() * 3 + 1,
      color: ['rgba(214, 138, 228, 0.8)', 'rgba(149, 93, 184, 0.8)', 'rgba(91, 140, 201, 0.8)'][Math.floor(Math.random() * 3)],
      connections: []
    }));

    return () => {
      window.removeEventListener('resize', resizeCanvas);
    };
  }, []);

  // Mouse move handler
  useEffect(() => {
    const handleMouseMove = (e) => {
      setMousePos({
        x: (e.clientX / window.innerWidth) * 2 - 1,
        y: (e.clientY / window.innerHeight) * 2 - 1
      });
    };

    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      setScrollY(currentScrollY);

      // Calculate sphere scale based on scroll position
      if (metaverseSectionRef.current) {
        const sectionTop = metaverseSectionRef.current.offsetTop;
        const sectionHeight = metaverseSectionRef.current.offsetHeight;
        const scrollProgress = (currentScrollY - sectionTop + window.innerHeight) / (sectionHeight + window.innerHeight);
        
        // Scale from 0.3 to 2 as user scrolls through the section
        const newScale = Math.min(Math.max(0.3 + scrollProgress * 2, 0.3), 2);
        setSphereScale(newScale);
      }
    };

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('scroll', handleScroll);

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  // Animation loop
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    
    const animate = () => {
      timeRef.current += 0.01;
      const time = timeRef.current;

      // Clear canvas with trail effect
      ctx.fillStyle = 'rgba(0, 0, 0, 0.05)';
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      // Draw grid background
      ctx.strokeStyle = 'rgba(91, 140, 201, 0.15)';
      ctx.lineWidth = 1;
      const gridSize = 50;
      const offsetX = (mousePos.x * 50 + scrollY * 0.5) % gridSize;
      const offsetY = (mousePos.y * 50 + scrollY * 0.3) % gridSize;

      for (let x = -gridSize; x < canvas.width + gridSize; x += gridSize) {
        ctx.beginPath();
        ctx.moveTo(x + offsetX, 0);
        ctx.lineTo(x + offsetX, canvas.height);
        ctx.stroke();
      }

      for (let y = -gridSize; y < canvas.height + gridSize; y += gridSize) {
        ctx.beginPath();
        ctx.moveTo(0, y + offsetY);
        ctx.lineTo(canvas.width, y + offsetY);
        ctx.stroke();
      }

      // Update and draw particles
      const particles = particlesRef.current;
      
      particles.forEach((particle, i) => {
        // Update position with mouse influence
        particle.x += particle.vx + mousePos.x * 0.5;
        particle.y += particle.vy + mousePos.y * 0.5;
        particle.z += particle.vz + Math.sin(time + i) * 0.3;

        // Wrap around edges
        if (particle.x < 0) particle.x = canvas.width;
        if (particle.x > canvas.width) particle.x = 0;
        if (particle.y < 0) particle.y = canvas.height;
        if (particle.y > canvas.height) particle.y = 0;
        if (particle.z < 0) particle.z = 1000;
        if (particle.z > 1000) particle.z = 0;

        // Calculate 3D projection
        const scale = 500 / (500 + particle.z);
        const x2d = particle.x + (particle.x - canvas.width / 2) * scale * 0.3;
        const y2d = particle.y + (particle.y - canvas.height / 2) * scale * 0.3;

        // Draw connections
        ctx.strokeStyle = particle.color.replace('0.8', '0.3');
        ctx.lineWidth = 0.5;

        particles.forEach((other, j) => {
          if (i >= j) return;
          const dx = particle.x - other.x;
          const dy = particle.y - other.y;
          const dz = particle.z - other.z;
          const dist = Math.sqrt(dx * dx + dy * dy + dz * dz);

          if (dist < 150) {
            const otherScale = 500 / (500 + other.z);
            const otherX2d = other.x + (other.x - canvas.width / 2) * otherScale * 0.3;
            const otherY2d = other.y + (other.y - canvas.height / 2) * otherScale * 0.3;

            ctx.beginPath();
            ctx.moveTo(x2d, y2d);
            ctx.lineTo(otherX2d, otherY2d);
            ctx.stroke();
          }
        });

        // Draw particle
        ctx.fillStyle = particle.color;
        ctx.beginPath();
        ctx.arc(x2d, y2d, particle.size * scale, 0, Math.PI * 2);
        ctx.fill();

        // Add glow effect
        const gradient = ctx.createRadialGradient(x2d, y2d, 0, x2d, y2d, particle.size * scale * 3);
        gradient.addColorStop(0, particle.color.replace('0.8', '0.4'));
        gradient.addColorStop(1, particle.color.replace('0.8', '0'));
        ctx.fillStyle = gradient;
        ctx.beginPath();
        ctx.arc(x2d, y2d, particle.size * scale * 3, 0, Math.PI * 2);
        ctx.fill();
      });

      // Draw floating orbs
      for (let i = 0; i < 5; i++) {
        const orbX = canvas.width / 2 + Math.sin(time * 0.5 + i) * 200 + mousePos.x * 100;
        const orbY = canvas.height / 2 + Math.cos(time * 0.3 + i) * 200 + mousePos.y * 100;
        const orbSize = 40 + Math.sin(time + i) * 20;

        const orbGradient = ctx.createRadialGradient(orbX, orbY, 0, orbX, orbY, orbSize);
        const colors = ['rgba(214, 138, 228, 0.3)', 'rgba(149, 93, 184, 0.3)', 'rgba(91, 140, 201, 0.3)'];
        const colorsTransparent = ['rgba(214, 138, 228, 0)', 'rgba(149, 93, 184, 0)', 'rgba(91, 140, 201, 0)'];
        orbGradient.addColorStop(0, colors[i % 3]);
        orbGradient.addColorStop(1, colorsTransparent[i % 3]);
        
        ctx.fillStyle = orbGradient;
        ctx.beginPath();
        ctx.arc(orbX, orbY, orbSize, 0, Math.PI * 2);
        ctx.fill();
      }

      animationRef.current = requestAnimationFrame(animate);
    };

    animate();

    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [mousePos, scrollY]);

  return (
    <div className="App" ref={containerRef}>
      <canvas ref={canvasRef} className="canvas-background" />
      
      <div className="scroll-container">
        <section className="hero-section">
          <div 
            className="logo-container"
            style={{
              transform: `
                perspective(1000px)
                rotateX(${mousePos.y * 10}deg)
                rotateY(${mousePos.x * 10}deg)
                translateZ(${scrollY * 0.5}px)
              `
            }}
          >
            <a href="https://alacticai.com" target="_blank" rel="noopener noreferrer" className="logo-link">
              <h1 className="logo-text">
                <span className="letter" style={{ '--i': 0 }}>A</span>
                <span className="letter" style={{ '--i': 1 }}>L</span>
                <span className="letter" style={{ '--i': 2 }}>A</span>
                <span className="letter" style={{ '--i': 3 }}>C</span>
                <span className="letter" style={{ '--i': 4 }}>T</span>
                <span className="letter" style={{ '--i': 5 }}>I</span>
                <span className="letter" style={{ '--i': 6 }}>C</span>
                <span className="letter letter-space" style={{ '--i': 7 }}> </span>
                <span className="letter" style={{ '--i': 8 }}>I</span>
                <span className="letter" style={{ '--i': 9 }}>N</span>
                <span className="letter" style={{ '--i': 10 }}>C</span>
              </h1>
            </a>
            <div className="logo-glow"></div>
          </div>

          <div className="floating-shapes">
            {[...Array(8)].map((_, i) => (
              <div
                key={i}
                className="shape"
                style={{
                  '--delay': `${i * 0.5}s`,
                  '--duration': `${15 + i * 2}s`,
                  left: `${10 + i * 12}%`,
                  top: `${20 + (i % 3) * 25}%`
                }}
              />
            ))}
          </div>
        </section>

        <section className="bridge-section">
          <div className="bridge-container">
            <div 
              className="bridge-tunnel"
              style={{
                transform: `translateZ(${-scrollY * 2}px) rotateX(${mousePos.y * 5}deg)`
              }}
            >
              <div className="tunnel-ring" style={{ '--depth': 0 }}></div>
              <div className="tunnel-ring" style={{ '--depth': 1 }}></div>
              <div className="tunnel-ring" style={{ '--depth': 2 }}></div>
              <div className="tunnel-ring" style={{ '--depth': 3 }}></div>
              <div className="tunnel-ring" style={{ '--depth': 4 }}></div>
              <div className="tunnel-ring" style={{ '--depth': 5 }}></div>
              <div className="tunnel-ring" style={{ '--depth': 6 }}></div>
              <div className="tunnel-ring" style={{ '--depth': 7 }}></div>
            </div>
            <div className="bridge-particles">
              {[...Array(30)].map((_, i) => (
                <div
                  key={i}
                  className="bridge-particle"
                  style={{
                    '--particle-delay': `${i * 0.2}s`,
                    '--particle-x': `${Math.random() * 100}%`,
                    '--particle-duration': `${3 + Math.random() * 2}s`
                  }}
                />
              ))}
            </div>
          </div>
          <div className="fade-to-darkness">
            <div className="sparkle-layer">
              {[...Array(50)].map((_, i) => (
                <div
                  key={i}
                  className="sparkle"
                  style={{
                    '--sparkle-x': `${Math.random() * 100}%`,
                    '--sparkle-y': `${Math.random() * 100}%`,
                    '--sparkle-delay': `${Math.random() * 3}s`,
                    '--sparkle-duration': `${1 + Math.random() * 2}s`
                  }}
                />
              ))}
            </div>
          </div>
        </section>

        <section className="transition-blur-section">
          <div className="blur-gradient-overlay"></div>
          <div className="sparkle-fade-container">
            {[...Array(100)].map((_, i) => (
              <div
                key={i}
                className="floating-sparkle"
                style={{
                  '--float-x': `${Math.random() * 100}%`,
                  '--float-y': `${20 + Math.random() * 60}%`,
                  '--float-delay': `${Math.random() * 4}s`,
                  '--float-duration': `${3 + Math.random() * 3}s`,
                  '--float-size': `${2 + Math.random() * 4}px`
                }}
              />
            ))}
          </div>
          
          <div className="content-section-wrapper">
            <div className="futuristic-text-container">
              <div className="text-glitch-overlay"></div>
              <div className="text-scan-lines"></div>
              
              <h2 className="section-heading">
                <span className="heading-word" style={{ '--word-delay': '0s' }}>ABOUT</span>
                <span className="heading-word brand-heading" style={{ '--word-delay': '0.2s' }}>ALACTIC</span>
                <span className="heading-word" style={{ '--word-delay': '0.4s' }}>INC</span>
              </h2>

              <p className="futuristic-paragraph">
                <span className="typewriter-line line-1">
                  <span className="word" style={{ '--word-delay': '0.8s' }}>Our</span>
                  <span className="word" style={{ '--word-delay': '0.95s' }}>mission</span>
                  <span className="word" style={{ '--word-delay': '1.1s' }}>is</span>
                  <span className="word" style={{ '--word-delay': '1.25s' }}>to</span>
                  <span className="word" style={{ '--word-delay': '1.4s' }}>make</span>
                  <span className="word highlight-word" style={{ '--word-delay': '1.55s' }}>AI</span>
                  <span className="word highlight-word" style={{ '--word-delay': '1.7s' }}>development</span>
                </span>
              </p>
              
              <p className="futuristic-paragraph">
                <span className="typewriter-line line-2">
                  <span className="word highlight-word" style={{ '--word-delay': '2s' }}>intuitive,</span>
                  <span className="word highlight-word" style={{ '--word-delay': '2.15s' }}>accessible,</span>
                  <span className="word" style={{ '--word-delay': '2.3s' }}>and</span>
                  <span className="word highlight-word" style={{ '--word-delay': '2.45s' }}>faster</span>
                  <span className="word" style={{ '--word-delay': '2.6s' }}>than</span>
                  <span className="word" style={{ '--word-delay': '2.75s' }}>ever</span>
                  <span className="word" style={{ '--word-delay': '2.9s' }}>before.</span>
                </span>
              </p>
              
              <p className="futuristic-paragraph">
                <span className="typewriter-line line-3">
                  <span className="word" style={{ '--word-delay': '3.2s' }}>Designed</span>
                  <span className="word" style={{ '--word-delay': '3.35s' }}>for</span>
                  <span className="word highlight-word" style={{ '--word-delay': '3.5s' }}>developers</span>
                  <span className="word" style={{ '--word-delay': '3.65s' }}>of</span>
                  <span className="word" style={{ '--word-delay': '3.8s' }}>all</span>
                  <span className="word" style={{ '--word-delay': '3.95s' }}>skill</span>
                  <span className="word" style={{ '--word-delay': '4.1s' }}>levels,</span>
                </span>
              </p>
              
              <p className="futuristic-paragraph">
                <span className="typewriter-line line-4">
                  <span className="word brand-word" style={{ '--word-delay': '4.4s' }}>Alactic</span>
                  <span className="word" style={{ '--word-delay': '4.55s' }}>makes</span>
                  <span className="word" style={{ '--word-delay': '4.7s' }}>learning</span>
                  <span className="word" style={{ '--word-delay': '4.85s' }}>and</span>
                  <span className="word" style={{ '--word-delay': '5s' }}>implementing</span>
                  <span className="word highlight-word" style={{ '--word-delay': '5.15s' }}>AI</span>
                  <span className="word highlight-word" style={{ '--word-delay': '5.3s' }}>development</span>
                  <span className="word highlight-word" style={{ '--word-delay': '5.45s' }}>effortless.</span>
                </span>
              </p>

              <div className="data-streams">
                {[...Array(6)].map((_, i) => (
                  <div
                    key={i}
                    className="data-stream"
                    style={{
                      '--stream-delay': `${i * 0.3}s`,
                      left: `${10 + i * 15}%`
                    }}
                  >
                    {[...Array(20)].map((_, j) => (
                      <div key={j} className="data-bit" style={{ '--bit-delay': `${j * 0.1}s` }}>
                        {Math.random() > 0.5 ? '1' : '0'}
                      </div>
                    ))}
                  </div>
                ))}
              </div>

              <div className="corner-brackets">
                <div className="bracket top-left"></div>
                <div className="bracket top-right"></div>
                <div className="bracket bottom-left"></div>
                <div className="bracket bottom-right"></div>
              </div>
            </div>
          </div>
        </section>

        <section className="metaverse-360-section" ref={metaverseSectionRef}>
          <div className="darkness-veil"></div>
          <div 
            className="sphere-container"
            style={{
              transform: `
                perspective(2000px)
                scale(${sphereScale})
              `
            }}
          >
            <div 
              className="sphere-wrapper"
              style={{
                transform: `rotateY(${scrollY * 0.15}deg) rotateX(${scrollY * 0.1}deg)`
              }}
            >
              {[...Array(12)].map((_, i) => {
                // Calculate scroll-based spread for nodes
                const angleOffset = i * 30;
                const spreadMultiplier = sphereScale > 1 ? (sphereScale - 1) * 200 : 0;
                const isLeftSide = (angleOffset >= 90 && angleOffset <= 270);
                const horizontalSpread = isLeftSide ? -spreadMultiplier : spreadMultiplier;
                
                return (
                  <div
                    key={i}
                    className="sphere-ring"
                    style={{
                      '--ring-index': i,
                      transform: `rotateY(${angleOffset}deg) translateZ(${300 + spreadMultiplier}px) translateX(${horizontalSpread}px)`
                    }}
                  >
                    {[...Array(8)].map((_, j) => {
                      const verticalAngle = j * 45;
                      const isTop = verticalAngle < 90 || verticalAngle > 270;
                      const isBottom = verticalAngle > 90 && verticalAngle < 270;
                      const verticalSpread = isTop ? -spreadMultiplier * 0.5 : (isBottom ? spreadMultiplier * 0.5 : 0);
                      
                      return (
                        <div
                          key={j}
                          className="sphere-node"
                          style={{
                            '--node-index': j,
                            transform: `rotateX(${verticalAngle}deg) translateZ(${150 + spreadMultiplier * 0.5}px) translateY(${verticalSpread}px)`,
                            width: `${15 * (sphereScale > 1 ? 1.5 : 1)}px`,
                            height: `${15 * (sphereScale > 1 ? 1.5 : 1)}px`
                          }}
                        />
                      );
                    })}
                  </div>
                );
              })}
              <div 
                className="sphere-core"
                style={{
                  width: `${100 * sphereScale}px`,
                  height: `${100 * sphereScale}px`
                }}
              ></div>
              <div 
                className="sphere-glow"
                style={{
                  width: `${400 * sphereScale}px`,
                  height: `${400 * sphereScale}px`
                }}
              ></div>
            </div>
          </div>
          <div 
            className="emergence-text"
            style={{
              opacity: sphereScale > 1.2 ? 1 : (sphereScale - 0.3) / 0.9
            }}
          >
            <span className="emerge-word" style={{ '--word-delay': '0s' }}>SIMPLIFYING</span>
            <span className="emerge-word" style={{ '--word-delay': '0.3s' }}>AI</span>
            <span className="emerge-word" style={{ '--word-delay': '0.6s' }}>DEVELOPMENT</span>
          </div>
          <div className="emergence-sparkles">
            {[...Array(80)].map((_, i) => (
              <div
                key={i}
                className="emergence-sparkle"
                style={{
                  '--emerge-x': `${Math.random() * 100}%`,
                  '--emerge-y': `${Math.random() * 100}%`,
                  '--emerge-delay': `${Math.random() * 2}s`,
                  '--emerge-duration': `${2 + Math.random() * 2}s`
                }}
              />
            ))}
          </div>
        </section>

        <section className="scroll-section section-3">
          <div className="portal-effect">
            <div className="portal-ring ring-1"></div>
            <div className="portal-ring ring-2"></div>
            <div className="portal-ring ring-3"></div>
            <div className="portal-center"></div>
          </div>
          <div className="final-message">
            <h2 className="agi-announcement">
              <span className="agi-word" style={{ '--word-delay': '0s' }}>ALACTIC</span>
              <span className="agi-word highlight-agi" style={{ '--word-delay': '0.3s' }}>AGI</span>
              <span className="agi-word" style={{ '--word-delay': '0.6s' }}>COMING</span>
              <span className="agi-word" style={{ '--word-delay': '0.9s' }}>SOON</span>
            </h2>
          </div>
        </section>
      </div>

      <div className="cursor-follower" style={{
        left: `${(mousePos.x + 1) * 50}%`,
        top: `${(mousePos.y + 1) * 50}%`
      }}></div>
    </div>
  );
}

export default App;
